import { useReducer } from 'react';
import type {
  AdresseInput,
  NeuerKundeInput,
  PositionInput,
  KundeSuchTreffer
} from '@/types/bestellung';
import { emptyAdresse, emptyNeuerKunde } from '@/types/bestellung';
import type { Zustellweg } from './ZustellwegPicker';
import type { Lieferwunsch } from '@/types/detail';
import { LIEFERWUNSCH_VORGABE } from '@/lib/lieferwunsch';

export type FormState = {
  bestellwegId: number | null;
  // Die Pflichtwahl aus E20: Abholung oder Versand. `null`, solange
  // niemand gewaehlt hat.
  zustellweg: Zustellweg | null;
  // Die optionale Praezisierung bei Versand. `null` heisst "noch offen"
  // und ist ausdruecklich erlaubt — beim Packen weiss man erst, ob DPD
  // oder Post.
  versandartId: number | null;
  // Der Geschenkwunsch (A25). Er steht bei der Zustellweg-Frage, weil
  // dort ohnehin ueber den Ausgang entschieden wird — und weil er, anders
  // als das Haekchen am Packtisch, aus dem GESPRAECH stammt.
  istGeschenk: boolean;
  // Der Kundenwunsch aus dem Gespraech (A56, #135) — zwei Angaben, die
  // auf jeder Karteikarte standen: wann geliefert werden soll, und ob
  // eine Nachricht gewuenscht ist. Sie stehen im zurueckhaltenden
  // Bereich, weil sie selten vorkommen; der Wunsch je POSITION steht an
  // der Position.
  lieferwunsch: Lieferwunsch;
  nachrichtWunsch: boolean;
  kundeId: number | null;
  kundeRef: KundeSuchTreffer | null;
  neuerKunde: NeuerKundeInput | null;
  rechnungAbweichend: boolean;
  rechnungAdresse: AdresseInput;
  notiz: string;
  // Kalenderdatum "YYYY-MM-DD" oder leer. Optional — viele Bestellungen
  // haben beim Erfassen noch keinen vereinbarten Abholtermin (B48).
  abholdatum: string;
  positionen: PositionInput[];
  // Inhalt der noch nicht bestaetigten Eingabefelder. Gehoert in den
  // State, damit ein Entwurf auch das erfasst, was getippt, aber noch
  // nicht per Lookup bzw. "Zufuegen" uebernommen wurde (Befund 43).
  entwurfIsbn: string;
  entwurfFreitext: string;
};

export type Action =
  | { type: 'SET_BESTELLWEG'; id: number }
  | { type: 'SET_ZUSTELLWEG'; weg: Zustellweg }
  | { type: 'SET_VERSANDART'; id: number | null }
  | { type: 'TOGGLE_GESCHENK' }
  | { type: 'SET_LIEFERWUNSCH'; wunsch: Lieferwunsch }
  | { type: 'TOGGLE_NACHRICHT_WUNSCH' }
  | { type: 'SET_KUNDE'; kunde: KundeSuchTreffer }
  | { type: 'CLEAR_KUNDE' }
  | { type: 'START_NEUER_KUNDE' }
  | { type: 'UPDATE_NEUER_KUNDE'; patch: Partial<NeuerKundeInput> }
  | { type: 'CLEAR_NEUER_KUNDE' }
  | { type: 'TOGGLE_RECHNUNG_ABWEICHEND' }
  | { type: 'UPDATE_RECHNUNG_ADRESSE'; patch: Partial<AdresseInput> }
  | { type: 'SET_NOTIZ'; text: string }
  | { type: 'SET_ABHOLDATUM'; datum: string }
  | { type: 'SET_ENTWURF_ISBN'; text: string }
  | { type: 'SET_ENTWURF_FREITEXT'; text: string }
  | { type: 'ADD_POSITION'; position: PositionInput }
  | { type: 'UPDATE_POSITION'; uid: string; patch: Partial<PositionInput> }
  | { type: 'REMOVE_POSITION'; uid: string }
  | { type: 'RESTORE'; state: FormState }
  | { type: 'RESET' };

export const initialState: FormState = {
  bestellwegId: null,
  zustellweg: null,
  versandartId: null,
  istGeschenk: false,
  // Die Vorgabe ist der haeufigere Fall, wenn niemand etwas sagt.
  lieferwunsch: LIEFERWUNSCH_VORGABE,
  nachrichtWunsch: false,
  kundeId: null,
  kundeRef: null,
  neuerKunde: null,
  rechnungAbweichend: false,
  rechnungAdresse: emptyAdresse(),
  notiz: '',
  abholdatum: '',
  positionen: [],
  entwurfIsbn: '',
  entwurfFreitext: ''
};

export function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case 'SET_BESTELLWEG':
      return { ...state, bestellwegId: action.id };
    case 'SET_ZUSTELLWEG':
      // Beim Wechsel auf Abholung faellt die Praezisierung weg — eine
      // Abholung hat keine Versandart, und ein stehengebliebenes "DPD"
      // waere eine Angabe ueber etwas, das nicht stattfindet.
      return {
        ...state,
        zustellweg: action.weg,
        versandartId: action.weg === 'abholung' ? null : state.versandartId
      };
    case 'SET_VERSANDART':
      return { ...state, versandartId: action.id };

    case 'TOGGLE_GESCHENK':
      // Der Wunsch haengt NICHT am Zustellweg: auch eine Abholung kann ein
      // Geschenk sein, das verpackt bereitliegen soll. Deshalb bleibt er
      // beim Wechsel von Versand auf Abholung stehen.
      return { ...state, istGeschenk: !state.istGeschenk };

    case 'SET_LIEFERWUNSCH':
      return { ...state, lieferwunsch: action.wunsch };
    case 'TOGGLE_NACHRICHT_WUNSCH':
      return { ...state, nachrichtWunsch: !state.nachrichtWunsch };
    case 'SET_KUNDE':
      return { ...state, kundeId: action.kunde.id, kundeRef: action.kunde, neuerKunde: null };
    case 'CLEAR_KUNDE':
      return { ...state, kundeId: null, kundeRef: null };
    case 'START_NEUER_KUNDE':
      return {
        ...state,
        kundeId: null,
        kundeRef: null,
        neuerKunde: state.neuerKunde ?? emptyNeuerKunde()
      };
    case 'UPDATE_NEUER_KUNDE':
      return {
        ...state,
        neuerKunde: { ...(state.neuerKunde ?? emptyNeuerKunde()), ...action.patch }
      };
    case 'CLEAR_NEUER_KUNDE':
      return { ...state, neuerKunde: null };
    case 'TOGGLE_RECHNUNG_ABWEICHEND':
      return { ...state, rechnungAbweichend: !state.rechnungAbweichend };
    case 'UPDATE_RECHNUNG_ADRESSE':
      return { ...state, rechnungAdresse: { ...state.rechnungAdresse, ...action.patch } };
    case 'SET_NOTIZ':
      return { ...state, notiz: action.text };
    case 'SET_ABHOLDATUM':
      return { ...state, abholdatum: action.datum };
    case 'SET_ENTWURF_ISBN':
      return { ...state, entwurfIsbn: action.text };
    case 'SET_ENTWURF_FREITEXT':
      return { ...state, entwurfFreitext: action.text };
    case 'ADD_POSITION':
      return { ...state, positionen: [...state.positionen, action.position] };
    case 'UPDATE_POSITION':
      return {
        ...state,
        positionen: state.positionen.map((p) =>
          p._uid === action.uid ? mengeMitfuehren({ ...p, ...action.patch }, p, action.patch) : p
        )
      };
    case 'REMOVE_POSITION':
      return { ...state, positionen: state.positionen.filter((p) => p._uid !== action.uid) };
    // Wiederhergestellter Entwurf (S-1). Der bisherige Stand wird komplett
    // ersetzt, nicht gemischt — ein halb uebernommener Entwurf waere
    // schlimmer als gar keiner. Die Leerwerte aus initialState dienen nur
    // als Boden fuer Entwuerfe aus einer aelteren Version, denen ein
    // inzwischen hinzugekommenes Feld fehlt (sonst wird das Input
    // uncontrolled).
    case 'RESTORE':
      return {
        ...initialState,
        ...action.state,
        // Positionen aus einem Entwurf, der vor A4 gesichert wurde, haben
        // kein `beschaffungen`. Ohne diese Zeile stünde dort `undefined`,
        // und die erste Karte, die darüber läuft, stürzt ab. Der Boden aus
        // `initialState` hilft hier nicht: er greift nur eine Ebene tief.
        positionen: (action.state.positionen ?? []).map((p) => ({
          ...p,
          beschaffungen: p.beschaffungen ?? []
        }))
      };
    case 'RESET':
      return initialState;
  }
}

/**
 * Die Zuordnung wandert mit der Menge mit — solange die Position GENAU
 * EINEN Weg trägt (A19.2).
 *
 * DER UNTERSCHIED ZUR DETAILANSICHT: Dort ist die Menge längst
 * gespeichert und der Schnellweg ordnet die offene Menge einmalig zu.
 * Beim Erfassen ist sie gerade erst getippt. Wer „Libri" antippt und
 * danach die Menge von 1 auf 3 stellt, hat nicht gesagt „einer davon
 * geht über Libri" — er hat gesagt „diese Position geht über Libri".
 * Die Zahl war eine Folge der Menge, keine eigene Aussage.
 *
 * Ohne diese Regel stünde nach dem Hochzählen „1× Libri, 2 offen", und
 * wer das nicht bemerkt, legt eine Bestellung an, bei der zwei von drei
 * Exemplaren nirgends bestellt sind. Bemerken müsste man es an einer
 * Rechnung, die niemand im Kopf mitführt.
 *
 * BEI ZWEI ODER MEHR WEGEN WIRD NICHT GERATEN. Die Aufteilung („2 bei
 * Libri, 3 bei Umbreit") ist eine Aussage des Menschen. Welchen der
 * beiden sollte die Oberfläche wachsen lassen? Jede Antwort wäre
 * geraten, und eine geratene Aufteilung sieht aus wie eine erfasste.
 *
 * SINKT DIE MENGE UNTER DAS ZUGEORDNETE, rechnet hier nichts um — das
 * kann nur im Mehr-Wege-Fall passieren, und dort gilt derselbe Satz.
 * Stattdessen sagt es formErrors(), und das Speichern bleibt gesperrt,
 * bis jemand einen Weg anpasst. Der Grund steht dort.
 *
 * Greift nur, wenn die Menge WIRKLICH im Patch steht: ein Patch über
 * `beschaffungen` (das Formular, die Schnellwege) darf die eben
 * gesetzte Zuordnung nicht sofort wieder überschreiben.
 */
function mengeMitfuehren(
  neu: PositionInput,
  alt: PositionInput,
  patch: Partial<PositionInput>
): PositionInput {
  if (patch.menge === undefined) return neu;
  if (alt.beschaffungen.length !== 1) return neu;
  if (neu.beschaffungen.length !== 1) return neu;
  if (neu.beschaffungen[0].menge === neu.menge) return neu;
  return {
    ...neu,
    beschaffungen: [{ ...neu.beschaffungen[0], menge: neu.menge }]
  };
}

/**
 * Wieviel an dieser Position mehr zugeordnet ist, als bestellt wird.
 *
 * 0 heißt: alles in Ordnung. Größer 0 heißt: der Server würde die
 * Bestellung beim Speichern mit `exceeds_menge` zurückweisen — und weil
 * eine durchgefallene Beschaffung die GANZE Bestellung zurückrollt
 * (api/lib/bestellung.php), wäre das nicht ein Feld in Rot, sondern das
 * ganze Formular verloren.
 */
export function beschaffungUeberhang(p: PositionInput): number {
  const zugeordnet = p.beschaffungen.reduce((s, b) => s + b.menge, 0);
  return Math.max(0, zugeordnet - p.menge);
}

// Ein Entwurf lohnt sich erst, wenn wirklich etwas drinsteht. Ein bloss
// geoeffnetes Formular soll beim naechsten Mal nichts anbieten.
export function istFormularLeer(s: FormState): boolean {
  return (
    s.bestellwegId === null &&
    s.zustellweg === null &&
    s.kundeId === null &&
    s.neuerKunde === null &&
    s.positionen.length === 0 &&
    s.notiz.trim() === '' &&
    s.abholdatum === '' &&
    !s.istGeschenk &&
    // Ein abweichender Kundenwunsch ist Inhalt: Wer ihn gesetzt und das
    // Formular weggeklickt hat, soll ihn beim naechsten Mal wiederfinden.
    s.lieferwunsch === LIEFERWUNSCH_VORGABE &&
    !s.nachrichtWunsch &&
    s.entwurfIsbn.trim() === '' &&
    s.entwurfFreitext.trim() === '' &&
    !s.rechnungAbweichend
  );
}

export function useBestellungReducer() {
  return useReducer(reducer, initialState);
}

// Feldbezogene Fehler statt eines blossen Booleans, damit die UI zeigen
// kann, WAS fehlt, statt den Speichern-Button stumm zu deaktivieren (B4).
export type FormErrors = {
  bestellweg?: string;
  zustellweg?: string;
  kunde?: string;
  positionen?: string;
};

export function formErrors(s: FormState): FormErrors {
  const e: FormErrors = {};

  if (s.bestellwegId === null) {
    e.bestellweg = 'Bestellweg wählen';
  }

  // Pflicht ist nur die Weiche, nicht die genaue Art (E20).
  if (s.zustellweg === null) {
    e.zustellweg = 'Abholung oder Versand wählen';
  }

  if (s.kundeId === null && s.neuerKunde === null) {
    e.kunde = 'Kunde auswählen oder neu anlegen';
  } else if (s.neuerKunde !== null) {
    const k = s.neuerKunde;
    const namePresent = k.nachname.trim() !== '' || k.vorname.trim() !== '';
    const firmaPresent = k.firma.trim() !== '';
    if (!namePresent && !firmaPresent) {
      e.kunde = 'Name oder Firma des neuen Kunden angeben';
    }
  }

  if (s.positionen.length === 0) {
    e.positionen = 'Mindestens eine Position hinzufügen';
  } else if (s.positionen.some((p) => p.titel.trim() === '' || p.menge < 1)) {
    e.positionen = 'Jede Position braucht einen Titel und Menge ≥ 1';
  } else {
    // Mehr zugeordnet als bestellt (A19.2). Nur im Mehr-Wege-Fall
    // erreichbar: bei genau einem Weg wandert die Zuordnung mit.
    //
    // Warum das SPEICHERN sperrt und nicht bloss ein Hinweis ist: Der
    // Server weist eine solche Beschaffung mit `exceeds_menge` ab, und
    // ein durchgefallener Eintrag rollt die ganze Bestellung zurück.
    // Ein Formular, das man abschicken darf und das danach alles
    // verwirft, ist schlimmer als eines, das vorher sagt, was nicht
    // stimmt.
    const zuviel = s.positionen.find((p) => beschaffungUeberhang(p) > 0);
    if (zuviel !== undefined) {
      e.positionen =
        `„${zuviel.titel.trim() === '' ? 'Eine Position' : zuviel.titel}": ` +
        `${zuviel.beschaffungen.reduce((n, b) => n + b.menge, 0)} zugeordnet, ` +
        `aber nur ${zuviel.menge} bestellt — bitte einen Weg anpassen`;
    }
  }

  return e;
}

export function isFormValid(s: FormState): boolean {
  return Object.keys(formErrors(s)).length === 0;
}

// Wandelt leere Strings im neuer-Kunde-Input in null fuer das Backend.
export function neuerKundeToWire(k: NeuerKundeInput) {
  const norm = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  return {
    anrede: norm(k.anrede),
    vorname: norm(k.vorname),
    nachname: norm(k.nachname),
    firma: norm(k.firma),
    email: norm(k.email),
    telefon: norm(k.telefon),
    bonus_kundennr: norm(k.bonus_kundennr),
    adresse_strasse: norm(k.adresse_strasse),
    adresse_plz: norm(k.adresse_plz),
    adresse_ort: norm(k.adresse_ort),
    adresse_land: norm(k.adresse_land),
    notiz: norm(k.notiz)
  };
}
